/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
// eslint-disable-next-line import/no-webpack-loader-syntax, import/no-unresolved
import videojs from "!video.js";

import { AdsEventManager } from "../../types";
import { AdTracker, CurrentAdTrackerVideoJS } from "../CurrentAdTracker";
import { PlayerDimension } from "../types";

// transpiling issue with video.js, !syntax with disabled webpack loader required
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
// eslint-disable-next-line import/no-webpack-loader-syntax, import/no-unresolved

export class EventManagerVideoJS implements AdsEventManager {
  private player: videojs.Player;
  private manager: google.ima.AdsManager;
  private currentAdTracker: CurrentAdTrackerVideoJS;
  private readonly startMuted: boolean;
  private adStarted = false;
  private adStartedWatchHandler?: number = undefined;

  constructor(
    manager: google.ima.AdsManager,
    player: videojs.Player,
    startMuted: boolean,
  ) {
    this.manager = manager;
    this.player = player;
    this.startMuted = startMuted;

    this.currentAdTracker = new CurrentAdTrackerVideoJS(manager, player);
    this.createEventListeners();
  }

  get adTracker(): AdTracker {
    return this.currentAdTracker.data;
  }

  get cuePoints(): number[] {
    return this.manager.getCuePoints();
  }

  volume(value: number) {
    this.manager.setVolume(value);
    this.currentAdTracker.muted(value === 0);
  }

  muted(state: boolean) {
    const currentVolume = this.manager.getVolume();

    // do not reset volume level when not muted
    if (!state && currentVolume !== 0) {
      return;
    }

    this.manager.setVolume(state ? 0 : 1);
    this.currentAdTracker.muted(state);
  }

  pause() {
    this.manager.pause();
    this.currentAdTracker.pause();
  }

  resume() {
    this.manager.resume();
    this.currentAdTracker.resume();
  }

  skip() {
    this.manager.skip();
  }

  initialize = (dimension: PlayerDimension, onInit: () => void) => {
    try {
      this.manager.init(
        dimension.width,
        dimension.height,
        google.ima.ViewMode.NORMAL,
      );

      onInit();

      this.manager.setVolume(this.player.volume());
      this.manager.start();
    } catch (adError) {
      this.onAdError(adError as google.ima.AdErrorEvent);
    }
  };

  dispose = () => {
    window.clearInterval(this.adStartedWatchHandler);

    this.manager.stop();
    this.currentAdTracker.stop();
    this.manager.destroy();
  };

  onPlayerResize = (width: number, height: number) => {
    this.manager.resize(width, height, google.ima.ViewMode.NORMAL);
  };

  onEnterFullScreen = (): void => {
    this.manager.resize(
      // iPadOS with Safari does not handle well window.screen
      // (width and height) with screen orientation, inner width and height
      // on window object is correctly handled
      window.innerWidth,
      window.innerHeight,
      google.ima.ViewMode.FULLSCREEN,
    );
  };

  onLeaveFullScreen = (width: number, height: number): void => {
    this.manager.resize(width, height, google.ima.ViewMode.NORMAL);
  };

  contentComplete = () => {
    const noPostroll =
      this.manager.getCuePoints() && !this.manager.getCuePoints().includes(-1);

    if (noPostroll) {
      this.player.trigger("nopostroll");
    }
  };

  private createEventListeners = () => {
    this.manager.addEventListener(
      google.ima.AdErrorEvent.Type.AD_ERROR,
      this.onAdError,
    );

    this.manager.addEventListener(
      google.ima.AdEvent.Type.LOADED,
      this.onAdDataLoaded,
    );

    this.manager.addEventListener(
      google.ima.AdEvent.Type.CONTENT_PAUSE_REQUESTED,
      this.onContentPauseRequested,
    );

    this.manager.addEventListener(
      google.ima.AdEvent.Type.CONTENT_RESUME_REQUESTED,
      this.onContentResumeRequested,
    );

    this.manager.addEventListener(
      google.ima.AdEvent.Type.ALL_ADS_COMPLETED,
      this.onAllAdsCompleted,
    );

    this.manager.addEventListener(
      google.ima.AdEvent.Type.STARTED,
      this.onAdStarted,
    );

    this.manager.addEventListener(
      google.ima.AdEvent.Type.COMPLETE,
      this.onAdComplete,
    );

    this.manager.addEventListener(
      google.ima.AdEvent.Type.SKIPPABLE_STATE_CHANGED,
      this.onSkipStateChange,
    );

    this.manager.addEventListener(
      google.ima.AdEvent.Type.SKIPPED,
      this.onAdComplete,
    );

    this.manager.addEventListener(
      google.ima.AdEvent.Type.PAUSED,
      this.onAdPaused,
    );

    this.manager.addEventListener(
      google.ima.AdEvent.Type.RESUMED,
      this.onAdResumed,
    );

    this.manager.addEventListener(google.ima.AdEvent.Type.LOG, this.onAdLog);
  };

  private onAdError = (event: google.ima.AdErrorEvent) => {
    console.error("Unable to load or process ad:", event);
    this.dispose();
  };

  private onAdDataLoaded = () => {
    this.player.trigger("adloaded");
  };

  private onContentPauseRequested = () => {
    this.player.pause();
    this.adStarted = false;

    if (this.startMuted) {
      this.player.muted(true);
      this.muted(true);
    }

    this.player.trigger("adloading");
    this.player.ads.startLinearAdMode();

    // if advertisement does not start after timeout, leave linear mode
    // to resume content
    this.adStartedWatchHandler = window.setTimeout(() => {
      if (!this.adStarted) {
        this.player.ads.endLinearAdMode();
      }
    }, 5000);
  };

  private onContentResumeRequested = () => {
    if (this.startMuted) {
      this.player.muted(false);
      this.muted(false);
    }

    this.player.trigger("adlinearmodeend");
    this.player.ads.endLinearAdMode();
  };

  private onAdStarted = (event: google.ima.AdEvent) => {
    this.adStarted = true;
    const currentAd = event.getAd();

    if (!currentAd?.isLinear()) {
      this.player.ads.skipLinearAdMode();
    }

    if (currentAd) {
      this.player.trigger("adstarted");
      this.currentAdTracker.start(currentAd);
    }
  };

  private onAdPaused = () => {
    this.player.trigger("adpaused");
  };

  private onAdResumed = () => {
    this.player.trigger("adresumed");
  };

  private onSkipStateChange = () => {
    if (this.manager.getAdSkippableState()) {
      this.player.trigger("adskipallowed");
    }
  };

  private onAdComplete = () => {
    this.currentAdTracker.stop();
    this.player.trigger("adcomplete");
  };

  private onAllAdsCompleted = () => {
    this.player.trigger("adallcompleted");
  };

  private onAdLog = (event: google.ima.AdEvent) => {
    console.log("Unable to play ad", event.getAdData());
  };
}
